/* #beta — 베타테스터 신청 페이지 (앱 셸 밖의 독립 화면).
   신청하기 → 구글 로그인으로 이메일 본인 확인 → 미리 심어둔 학원코드 자리 하나 선점 →
   codes/{이메일} 등록. 등록되면 학생이 로그인하는 순간 ADR-014 조회로 베타 권한이 켜진다. */
import React from "react";
import { _auth, _db } from "../core/platform.js";
import { ASKS_AGE, SCHOOLS, needsGuardian, registerBeta } from "../core/beta.js";
import { Prof } from "../ui/common.jsx";
const { useState, useRef } = React;

const APP_URL = "https://provee-mvp.pages.dev/#student";
// 구글 계정이어야 하지만 워크스페이스 계정은 회사 도메인을 쓴다 — 형식만 본다.
const okEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
const digits = (v) => (v || "").replace(/\D/g, "");

// 이름·아이콘은 한 곳에서만 관리한다. short는 신청 전(훑어보기), desc는 신청 후(실제 사용법)에 쓴다.
const MODES = [
  { ic: "🎓", name: "개념강의", short: "1:1 과외처럼 설명",
    desc: "개념을 처음 배울 때. AI가 1:1 과외처럼 눈높이에 맞춰 이해할 때까지 설명해줘요." },
  { ic: "🧠", name: "과정모드", short: "답이 아니라 풀이 과정을 채점",
    desc: "손글씨로 풀이 과정을 쓰면 답이 아니라 과정을 채점해요. 무엇을 몰랐는지 힌트를 줘서 스스로 풀 수 있게 도와요." },
  { ic: "📇", name: "플래시모드", short: "빠르게 풀고 바로 채점",
    desc: "OX·객관식·단답을 빠르게 풀고 바로 채점. 짧은 시간 반복 훈련에 좋아요." },
  { ic: "📝", name: "시험보기", short: "실전 시험 보고 점수 확인",
    desc: "객관식·단답·서술형이 섞인 실전 시험. 한 번에 채점하고 점수까지 확인합니다." },
];

// 2x2 사분면. 카드 최소폭을 360px으로 둬서 좁은 화면에서는 미디어쿼리 없이 한 줄씩 쌓인다.
const QUAD = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 14 };

const P = ({ children, style }) => <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.85, ...style }}>{children}</p>;

/* 랜딩 섹션 — 제목 위에 작은 라벨(eyebrow)을 둬서 줄글이 아니라 구획으로 읽히게 한다. */
function Section({ eyebrow, title, children, style }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 16, ...style }}>
      {(eyebrow || title) && (
        <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {eyebrow && (
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: "var(--pri)" }}>{eyebrow}</div>
          )}
          {title && <h2 className="jua" style={{ margin: 0, fontSize: 24, lineHeight: 1.35 }}>{title}</h2>}
        </header>
      )}
      {children}
    </section>
  );
}

const Card = ({ children }) => (
  <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
);
const Body = ({ children }) => <P style={{ fontSize: 13.5, lineHeight: 1.8, color: "var(--sub)" }}>{children}</P>;

/* 자료·학습모드 소개는 신청 전(랜딩)과 신청 후(안내) 양쪽에 같은 내용이 필요해 컴포넌트로 둔다. */
function Materials() {
  return (
    <>
      <div style={QUAD}>
        <Card>
          <div className="jua" style={{ fontSize: 17 }}>📚 교과서 단원 그대로</div>
          <Body>중1부터 고3 수능 범위까지 정규 교육과정 목차가 들어 있어요. 준비물 없이 단원만 고르면 바로 시작합니다.</Body>
        </Card>
        <Card>
          <div className="jua" style={{ fontSize: 17 }}>📎 내 자료 업로드</div>
          <Body>학교 프린트·문제집·필기 노트를 사진이나 PDF로 올리면 그걸로 공부해요. 손글씨도 AI가 읽습니다.</Body>
        </Card>
      </div>
      <div className="warn" style={{ padding: "10px 14px", fontSize: 13.5, lineHeight: 1.7 }}>
        직접 올린 자료로는 <b>과정모드·플래시모드</b>만 쓸 수 있어요.
      </div>
    </>
  );
}

function ModeCards() {
  return (
    <div style={QUAD}>
      {MODES.map((m) => (
        <Card key={m.name}>
          <div style={{ fontSize: 26, lineHeight: 1 }}>{m.ic}</div>
          <div className="jua" style={{ fontSize: 17 }}>{m.name}</div>
          <Body>{m.desc}</Body>
        </Card>
      ))}
    </div>
  );
}

/* 신청 전(랜딩)용 축약판 — 아직 신청할지 정하는 단계라 훑어보기만 되면 된다.
   자세한 설명은 신청 후 안내(Materials·ModeCards)가 맡는다. */
function MaterialsBrief() {
  return (
    <div style={QUAD}>
      <Card>
        <div className="jua" style={{ fontSize: 17 }}>📚 교과서 단원 그대로</div>
        <Body>중1부터 고3 수능까지. 준비물 없이 단원만 고르면 시작.</Body>
      </Card>
      <Card>
        <div className="jua" style={{ fontSize: 17 }}>📎 내 자료 업로드</div>
        <Body>프린트·문제집·필기 노트를 사진이나 PDF로. 손글씨도 읽어요.</Body>
      </Card>
    </div>
  );
}

function ModesBrief() {
  return (
    <div style={QUAD}>
      {MODES.map((m) => (
        <div key={m.name} className="card" style={{ padding: "16px 18px", display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ fontSize: 24, lineHeight: 1, flex: "0 0 auto" }}>{m.ic}</div>
          <div style={{ minWidth: 0 }}>
            <div className="jua" style={{ fontSize: 16 }}>{m.name}</div>
            <Body>{m.short}</Body>
          </div>
        </div>
      ))}
    </div>
  );
}

const STEPS = [
  { t: "Provee 링크 접속", d: APP_URL },
  { t: "구글 로그인 · 베타 권한 자동 활성화", d: "베타 신청할 때 적어주신 이메일의 구글 계정으로 로그인하면 권한이 바로 켜집니다. 코드를 입력하실 필요 없어요." },
  { t: "공부할 내용 정하기", d: "교과서 단원을 고르거나 내 자료를 올립니다.", to: "mat" },
  { t: "공부할 방식 정하기", d: "개념강의·과정모드·플래시모드·시험보기 중에서 고릅니다.", to: "mode" },
];

/* 신청이 끝난 뒤 보여줄 사용 안내 — 다 읽고 나면 같은 창에서 학생 화면으로 넘어간다. */
function Guide() {
  // 경로를 루트로 되돌린 뒤 해시를 바꾼다 — /beta로 들어온 사람은 해시만 바꾸면 주소가
  // /beta#student로 남아 신청이 끝났는데도 beta가 계속 붙어 있다(새로고침해도 화면은 정상).
  const go = () => { try{ history.replaceState(null,"","/"); }catch{} location.hash = "#student"; };
  // 3·4번 단계는 아래 해당 섹션으로 데려간다 — 설명을 두 번 쓰지 않으려고 링크로 잇는다.
  const secs = { mat: useRef(null), mode: useRef(null) };
  const jump = (k) => () => secs[k].current && secs[k].current.scrollIntoView({ behavior: "smooth", block: "start" });
  const cta = (
    <button className="btn pri" style={{ padding: "16px 30px", fontSize: 16 }} onClick={go}>
      지금 시작하기 →
    </button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
      <div
        className="card"
        style={{
          padding: "44px 28px 38px", textAlign: "center",
          background: "linear-gradient(170deg, var(--ok-bg) 0%, var(--card) 62%)",
          border: "2px solid var(--ok-bd)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
          <div style={{ fontSize: 46, lineHeight: 1 }}>🎉</div>
          <h1 className="jua" style={{ margin: 0, fontSize: 30, lineHeight: 1.35 }}>신청이 완료됐어요</h1>
          <P style={{ maxWidth: 420, color: "var(--sub)" }}>
            베타테스터 자리가 확보됐습니다.<br />
            아래 순서대로 시작하면 권한이 자동으로 켜져요.
          </P>
          {cta}
        </div>
      </div>

      <Section eyebrow="START" title="이렇게 시작하세요">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {STEPS.map((s, i) => {
            const body = (
              <>
                <div
                  className="jua"
                  style={{
                    flex: "0 0 auto", width: 38, height: 38, borderRadius: "50%", fontSize: 17,
                    background: "var(--pri)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >{i + 1}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0, flex: 1 }}>
                  <div className="jua" style={{ fontSize: 17 }}>{s.t}</div>
                  <Body><span style={{ wordBreak: "break-all" }}>{s.d}</span></Body>
                </div>
                {s.to && <div className="jua" style={{ flex: "0 0 auto", color: "var(--pri)", fontSize: 15 }}>보기 ↓</div>}
              </>
            );
            const box = { padding: 20, display: "flex", gap: 16, alignItems: "flex-start", textAlign: "left" };
            return s.to
              ? (
                <button
                  key={s.t} type="button" className="card" onClick={jump(s.to)}
                  style={{ ...box, width: "100%", fontFamily: "inherit", cursor: "pointer" }}
                >{body}</button>
              )
              : <div key={s.t} className="card" style={box}>{body}</div>;
          })}
        </div>
      </Section>

      <div ref={secs.mat} style={{ scrollMarginTop: 20 }}>
        <Section eyebrow="MATERIALS" title="무엇으로 공부하나요">
          <Materials />
        </Section>
      </div>

      <div ref={secs.mode} style={{ scrollMarginTop: 20 }}>
        <Section eyebrow="HOW IT WORKS" title="공부하는 네 가지 방식">
          <ModeCards />
        </Section>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, paddingBottom: 32 }}>
        {cta}
        <div className="hint">다 읽으셨으면 눌러주세요.</div>
      </div>
    </div>
  );
}

/* 히어로 — 첫 화면에서 "무엇을 주는지 + 무엇을 하면 되는지"만 남긴다. */
function Hero({ onApply }) {
  return (
    <div
      className="card"
      style={{
        padding: "44px 28px 38px", textAlign: "center", overflow: "hidden", position: "relative",
        background: "linear-gradient(170deg, var(--pri-s) 0%, var(--card) 62%)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
        <span className="chip" style={{ background: "var(--pri)", color: "#fff", fontSize: 12, padding: "6px 14px" }}>
          2일 집중 베타 · 선착순 마감
        </span>
        <Prof size={76} />
        <h1 className="jua" style={{ margin: 0, fontSize: 32, lineHeight: 1.35, letterSpacing: "-.01em" }}>
          설명할 수 있어야<br />진짜 아는 것이다
        </h1>
        <P style={{ maxWidth: 440, color: "var(--sub)" }}>
          답만 맞추는 공부는 끝.<br /><br />
          풀이 과정을 손글씨로 쓰면 <br />AI가 빈 개념을 찾아 채워주는<br />
          자기주도 수학 학습 서비스,<br /> <b style={{ color: "var(--ink)" }}>Provee</b>입니다.
        </P>
        <button className="btn pri" style={{ padding: "16px 30px", fontSize: 16, marginTop: 4 }} onClick={onApply}>
          베타테스터 신청하기 →
        </button>
        <div className="hint">신청은 1분, 구글 계정만 있으면 됩니다.</div>
      </div>
    </div>
  );
}

/* 혜택 — 랜딩에서 가장 강한 카드 하나로 몰아 준다. */
function Reward() {
  return (
    <div
      className="card"
      style={{
        padding: 28, display: "flex", flexDirection: "column", gap: 18, textAlign: "center",
        border: "2px solid var(--pri-bd)", background: "var(--paper)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".08em", color: "var(--pri)" }}>BETA REWARD</div>
      <div>
        <div className="jua" style={{ fontSize: 26, lineHeight: 1.4 }}>Provee 6개월 무료 이용권</div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 6 }}>
          정가 <span style={{ textDecoration: "line-through" }}>100,000원</span> → <b style={{ color: "var(--pri)" }}>0원</b>
        </div>
      </div>
      <div className="warn" style={{ padding: "16px 18px", textAlign: "left", display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="jua" style={{ fontSize: 16, fontWeight: 600 }}>지급 조건</div>
        <P style={{ fontSize: 15, lineHeight: 1.8 }}>
          <b style={{ fontSize: 17 }}>2일 동안 매일 2시간씩</b> 서비스를 사용해 주세요.
        </P>
        <P style={{ fontSize: 13.5, lineHeight: 1.8 }}>
          이번 베타는 2일간의 집중 데이터 수집 기간이라, 여러분의 실제 사용 기록과 피드백이 서비스를 만드는 데
          가장 큰 도움이 됩니다.
        </P>
      </div>
      <div
        style={{
          padding: "16px 18px", textAlign: "left", borderRadius: 14,
          background: "var(--pri-bg)", border: "1px solid var(--pri-bd)",
        }}
      >
        <P style={{ fontSize: 14.5, lineHeight: 1.85, color: "var(--pri-ink)" }}>
          내부적으로 <b>Claude</b>(백업으로 ChatGPT·Gemini)를 사용해 답변하기 때문에,
          <b style={{ fontSize: 16 }}> 사실상 6개월치 Claude 이용권</b>을 받는 것이나 다름없습니다.
        </P>
      </div>
    </div>
  );
}

function BetaApply() {
  const [f, setF] = useState({ name: "", phone: "", email: "", school: "", guardian: false, minorSelf: false });
  const [err, setErr] = useState(null);   // {at, msg} — at은 문구를 붙일 칸
  const [phase, setPhase] = useState("form");   // form | linking | done
  const [modalErr, setModalErr] = useState("");
  const [showInfo, setShowInfo] = useState(false);   // 서비스 설명 — 신청을 가리지 않게 접어 둔다
  const formRef = useRef(null);
  // 값을 고치면 안내 문구는 지운다 — 체크를 해놓고도 빨간 박스가 남아 있으면 안 된다.
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setErr(null);
    setF((p) => ({ ...p, [k]: v }));
  };
  const minor = needsGuardian(f.school) || (f.school === ASKS_AGE && f.minorSelf);
  const badEmail = f.email.trim() !== "" && !okEmail(f.email);
  const toForm = () => formRef.current && formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });

  // 어느 칸이 틀렸는지까지 돌려준다 — 보호자 동의처럼 화면 중간에 있는 항목은 그 자리에 문구를 띄운다.
  function validate() {
    if (!f.name.trim()) return { at: "", msg: "이름을 적어주세요." };
    if (digits(f.phone).length < 10 || digits(f.phone).length > 11) return { at: "", msg: "휴대폰 번호를 숫자 10~11자리로 적어주세요." };
    if (!okEmail(f.email)) return { at: "", msg: "이메일 형식을 확인해주세요." };
    if (!f.school) return { at: "", msg: "소속을 골라주세요." };
    if (minor && !f.guardian) return { at: "guardian", msg: "보호자 동의를 받으셨다면 위 항목을 체크해주세요." };
    return null;
  }

  async function submit() {
    const bad = validate();
    if (bad) { setErr(bad); return; }
    setErr(null); setModalErr(""); setPhase("linking");
    const email = f.email.trim().toLowerCase();
    try {
      if (!_auth || !_db) throw new Error("지금은 신청을 받을 수 없어요. 인터넷 연결을 확인하고 다시 시도해주세요.");
      // 익명 로그인 — 화면에는 아무 창도 뜨지 않는다. 자리를 잠글 uid만 받아오는 용도라
      // 이메일 소유 확인은 하지 않고, 대신 규칙이 "내 uid로 잠근 자리"만 읽게 막는다.
      const cred = _auth.currentUser ? { user: _auth.currentUser } : await _auth.signInAnonymously();
      // adult을 화면에서 넘긴다 — "기타"는 소속만으로 미성년 여부를 알 수 없다.
      await registerBeta(_db, { email, uid: cred.user.uid, name: f.name.trim(), phone: digits(f.phone),
        school: f.school, adult: !minor, guardian: f.guardian });
      setPhase("done");
      window.scrollTo(0, 0);
    } catch (e) {
      const code = e && e.code;
      // 익명 로그인이 콘솔에서 꺼져 있으면 여기로 떨어진다 — 운영자가 바로 알아보게 문구를 따로 둔다.
      if (code === "auth/operation-not-allowed" || code === "auth/admin-restricted-operation") {
        setModalErr("신청 접수가 아직 열리지 않았어요. 운영자에게 알려주세요. (익명 로그인 비활성)");
        return;
      }
      setModalErr((e && e.message) || "신청에 실패했어요. 잠시 뒤 다시 시도해주세요.");
    }
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="hd" style={{ marginBottom: 6 }}>
        <div className="brand">
          <Prof size={44} />
          <div>
            <h1>
              Provee<span style={{ color: "var(--pri)" }}>.</span>
            </h1>
            <div className="tag">베타테스터 신청</div>
          </div>
        </div>
        {phase !== "done" && (
          <button className="btn pri sm" onClick={toForm}>신청하기</button>
        )}
      </div>

      {phase === "done" ? <Guide /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
          <Hero onApply={toForm} />

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <button
              className="btn gho" onClick={() => setShowInfo((v) => !v)} aria-expanded={showInfo}
              style={{ alignSelf: "center", padding: "12px 22px", fontSize: 14.5 }}
            >
              {showInfo ? "서비스 설명 접기 ▴" : "서비스 설명 보기 ▾"}
            </button>
            {showInfo && (
              <>
                <Section eyebrow="MATERIALS" title="무엇으로 공부하나요">
                  <MaterialsBrief />
                </Section>
                <Section eyebrow="HOW IT WORKS" title="공부하는 네 가지 방식">
                  <ModesBrief />
                </Section>
              </>
            )}
          </div>

          <Section eyebrow="REWARD" title="베타테스터 혜택">
            <Reward />
          </Section>

          <Section eyebrow="APPLY" title="베타테스터 신청">
            <div ref={formRef} className="card" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12, scrollMarginTop: 20 }}>
              <div className="selectrow">
                <label htmlFor="b-name">이름</label>
                <input id="b-name" className="field" value={f.name} onChange={set("name")} placeholder="홍길동" />
              </div>
              <div className="selectrow">
                <label htmlFor="b-phone">휴대폰 번호</label>
                <input id="b-phone" className="field" value={f.phone} onChange={set("phone")} placeholder="01012345678" inputMode="numeric" />
              </div>
              <div className="selectrow">
                <label htmlFor="b-email">서비스 로그인에 사용할 <b>구글</b> 이메일</label>
                <input
                  id="b-email" className="field" value={f.email} onChange={set("email")}
                  placeholder="name@example.com" inputMode="email"
                  aria-invalid={badEmail}
                  style={badEmail ? { borderColor: "var(--err-ink)" } : undefined}
                />
                {badEmail
                  ? <span className="err">이메일 형식을 확인해주세요.</span>
                  : <span className="hint">구글 계정이 아니면 로그인이 안 돼요. 실제로 로그인할 구글 계정 주소를 적어주세요.</span>}
              </div>
              <div className="selectrow">
                <label htmlFor="b-school">소속</label>
                <select id="b-school" className="field" value={f.school} onChange={set("school")}>
                  <option value="">선택해주세요</option>
                  {SCHOOLS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {f.school === ASKS_AGE && (
                <label className="row" style={{ gap: 8, fontSize: 13.5, lineHeight: 1.6 }}>
                  <input type="checkbox" checked={f.minorSelf} onChange={set("minorSelf")} style={{ accentColor: "var(--pri)" }} />
                  <span>저는 미성년자예요.</span>
                </label>
              )}
              {minor && (
                <div
                  style={err && err.at === "guardian"
                    ? { padding: "12px 14px", borderRadius: 12, background: "var(--err-bg)", border: "1px solid var(--err-bd)" }
                    : undefined}
                >
                  <label className="row" style={{ gap: 8, fontSize: 13.5, lineHeight: 1.6 }}>
                    <input type="checkbox" checked={f.guardian} onChange={set("guardian")} style={{ accentColor: "var(--pri)" }} />
                    <span>연락처 제공에 보호자 동의를 받았습니다.</span>
                  </label>
                  {err && err.at === "guardian" && (
                    <div className="err" style={{ marginTop: 6 }}>⚠️ {err.msg}</div>
                  )}
                </div>
              )}
              {err && !err.at && <div className="err">{err.msg}</div>}
              <button className="btn pri" style={{ padding: "14px 20px", fontSize: 15.5 }} onClick={submit}>신청하기</button>
            </div>
          </Section>

          <div className="hint" style={{ textAlign: "center", paddingBottom: 32 }}>
            Provee · 설명할 수 있어야 진짜 아는 것이다
          </div>
        </div>
      )}

      {phase === "linking" && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "var(--scrim)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="card" style={{ padding: 28, maxWidth: 380, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            {modalErr ? (
              <>
                <div style={{ fontSize: 28 }}>⚠️</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.8 }}>{modalErr}</div>
                <div className="row" style={{ justifyContent: "center" }}>
                  <button className="btn gho sm" onClick={() => setPhase("form")}>돌아가기</button>
                  <button className="btn pri sm" onClick={submit}>다시 시도</button>
                </div>
              </>
            ) : (
              <>
                <div className="spinner" />
                <div style={{ fontSize: 15, lineHeight: 1.8 }}>베타테스터 자리를 잡는 중이에요</div>
                <div className="hint">잠시만 기다려주세요.</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { BetaApply };
